import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from 'react';
import {
  Play,
  Pause,
  SkipBack,
  SkipForward,
  FastForward,
  Rewind,
  Share2,
  List,
  X,
  Search,
  Clock,
  MapPin,
  Radio,
  Captions,
  Music,
  Loader2,
  Bookmark,
  FileText,
  Volume2,
  VolumeX,
  Check,
  LayoutGrid,
  BookOpen,
  Settings,
  ChevronUp,
  ChevronLeft,
  ChevronRight,
  Trash2,
  ChevronDown,
  Star,
  Users,
  Copy,
  Calendar,
  ArrowUpRight,
} from 'lucide-react';

// --- Configuration ---
// Hardcode your data URL here to load data by default without MOCK_DATA
const DEFAULT_DATA_URL = 'https://baws.in/assets/catalog/audio_data.json';

// --- Styles ---
const shakaStyles = `
  /* 1. Target Shaka's DOM-based rendering container */
  .shaka-text-container { 
    bottom: 20% !important;
    z-index: 50 !important; /* Ensure sits above overlays */
  } 
  
  /* Target the actual text spans created by Shaka */
  .shaka-text-container span {
    background-color: rgba(0, 0, 0, 1) !important; /* Solid Black */
    color: #FACC15 !important; /* Bright Yellow */
    text-shadow: 0px 0px 4px black !important;
    padding: 6px 12px !important; 
    border-radius: 6px !important;
    font-family: sans-serif !important;
    font-weight: 700 !important;
    font-size: 1.1rem !important;
    line-height: 1.4 !important;
  }

  /* 2. Target Native Browser Cues (Essential for Safari/Chrome fallback) */
  video::cue {
    background-color: rgba(0, 0, 0, 1) !important;
    color: #FACC15 !important;
    text-shadow: 0px 0px 4px black !important;
    font-family: sans-serif !important;
    font-weight: 700 !important;
  }

  /* Legacy Webkit selector just in case */
  video::-webkit-media-text-track-display {
    background-color: rgba(0, 0, 0, 1) !important;
    color: #FACC15 !important;
  }
  
  /* Custom Animations */
  @keyframes jiggle {
    0% { transform: rotate(-1deg); }
    50% { transform: rotate(1deg); }
    100% { transform: rotate(-1deg); }
  }
  .animate-jiggle {
    animation: jiggle 0.2s ease-in-out infinite;
  }
  
  @keyframes pulse-ring {
    0% { transform: scale(0.95); box-shadow: 0 0 0 0 rgba(59, 130, 246, 0.7); }
    70% { transform: scale(1); box-shadow: 0 0 0 10px rgba(59, 130, 246, 0); }
    100% { transform: scale(0.95); box-shadow: 0 0 0 0 rgba(59, 130, 246, 0); }
  }
  .animate-pulse-ring {
    animation: pulse-ring 2s infinite;
  }
`;

// --- Constants ---
const GAP_SECONDS = 10;
const HISTORY_KEY = 'baws_audio_history';
const QUEUE_KEY = 'baws_audio_queue';
const FAV_KEY = 'baws_audio_favorites';
const BOOKMARK_KEY = 'baws_audio_bookmarks';

// --- Data Helpers ---
const generateId = (item) => {
  if (item.id && item.id.toString().trim().length > 0) return item.id;
  const uniqueKey =
    item.dest || item.stream || item.audio || item.url || item.link;
  // CHANGED: .Speaker to .speaker and 'Unknown' to 'Sujataa'
  if (uniqueKey) return `${item.speaker || 'Sujataa'}_${uniqueKey}`;
  return `${item.title}_${item.date}`;
};

const processData = (data) => {
  if (!Array.isArray(data)) return [];
  const unique = new Map();

  data.forEach((item, index) => {
    const hasSource = item.stream || item.audio || item.dest || item.url;
    if (!hasSource) return;

    const normalizedItem = {
      ...item,
      stream: item.stream || item.url || item.audio,
      title: item.title || `Track ${index + 1}`,
    };

    let id = generateId(normalizedItem);

    if (unique.has(id)) {
      id = `${id}_${index}`;
    }

    unique.set(id, { ...normalizedItem, id });
  });

  return Array.from(unique.values());
};

// --- Helper to read history objects safely (handles legacy number format) ---
const getHistoryMeta = (history, id) => {
  if (!history || !id) return { progress: 0, lastPlayed: 0 };
  const entry = history[id];
  if (entry === undefined) return { progress: 0, lastPlayed: 0 };
  // Legacy support: if entry is just a number (the old progress), treat it as progress with 0 timestamp
  if (typeof entry === 'number') return { progress: entry, lastPlayed: 0 };
  return {
    progress: entry.progress || 0,
    lastPlayed: entry.lastPlayed || 0,
  };
};

// --- Link Parser Helper ---
const Linkify = ({ text }) => {
  if (!text || typeof text !== 'string') return null;
  const urlRegex = /(https?:\/\/[^\s]+)/g;
  const parts = text.split(urlRegex);

  return (
    <>
      {parts.map((part, i) => {
        if (part.match(urlRegex)) {
          return (
            <a
              key={i}
              href={part}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-400 hover:underline break-all"
              onClick={(e) => e.stopPropagation()}
            >
              {part}
            </a>
          );
        }
        return part;
      })}
    </>
  );
};

// --- Helpers ---
const getAudioContext = () => {
  if (!window.AudioContext && !window.webkitAudioContext) return null;
  if (!window._sharedAudioCtx) {
    window._sharedAudioCtx = new (window.AudioContext ||
      window.webkitAudioContext)();
  }
  return window._sharedAudioCtx;
};

// --- UPDATED: Robust RNG for Seeding ---
function cyrb128(str) {
  let h1 = 1779033703,
    h2 = 3144134277,
    h3 = 1013904242,
    h4 = 2773480762;
  for (let i = 0, k; i < str.length; i++) {
    k = str.charCodeAt(i);
    h1 = h2 ^ Math.imul(h1 ^ k, 597399067);
    h2 = h3 ^ Math.imul(h2 ^ k, 2869860233);
    h3 = h4 ^ Math.imul(h3 ^ k, 951274213);
    h4 = h1 ^ Math.imul(h4 ^ k, 2716044179);
  }
  h1 = Math.imul(h3 ^ (h1 >>> 18), 597399067);
  h2 = Math.imul(h4 ^ (h2 >>> 22), 2869860233);
  h3 = Math.imul(h1 ^ (h3 >>> 17), 951274213);
  h4 = Math.imul(h2 ^ (h4 >>> 19), 2716044179);
  return [
    (h1 ^ h2 ^ h3 ^ h4) >>> 0,
    (h2 ^ h1) >>> 0,
    (h3 ^ h1) >>> 0,
    (h4 ^ h1) >>> 0,
  ];
}

function sfc32(a, b, c, d) {
  return function () {
    a >>>= 0;
    b >>>= 0;
    c >>>= 0;
    d >>>= 0;
    var t = (a + b) | 0;
    a = b ^ (b >>> 9);
    b = (c + (c << 3)) | 0;
    c = (c << 21) | (c >>> 11);
    d = (d + 1) | 0;
    t = (t + d) | 0;
    c = (c + t) | 0;
    return (t >>> 0) / 4294967296;
  };
}

function shuffle(array, seedString) {
  const seed = cyrb128(seedString);
  const rng = sfc32(seed[0], seed[1], seed[2], seed[3]);
  let currentIndex = array.length,
    randomIndex;
  const newArray = [...array];
  while (currentIndex !== 0) {
    randomIndex = Math.floor(rng() * currentIndex);
    currentIndex--;
    [newArray[currentIndex], newArray[randomIndex]] = [
      newArray[randomIndex],
      newArray[currentIndex],
    ];
  }
  return newArray;
}

const playBlip = () => {
  try {
    const ctx = getAudioContext();
    if (!ctx) return;
    if (ctx.state === 'suspended') ctx.resume();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.frequency.setValueAtTime(800, ctx.currentTime);
    osc.frequency.exponentialRampToValueAtTime(400, ctx.currentTime + 0.1);
    gain.gain.setValueAtTime(0.05, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.1);
    osc.start();
    osc.stop(ctx.currentTime + 0.1);
  } catch (e) {}
};

const parseDuration = (str) => {
  if (!str) return 0;
  const parts = str.split(':').map(Number);
  if (parts.length === 2) return parts[0] * 60 + parts[1];
  if (parts.length === 3) return parts[0] * 3600 + parts[1] * 60 + parts[2];
  return 0;
};

const formatDurationString = (seconds) => {
  if (isNaN(seconds)) return '00:00';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  const mStr = m < 10 ? '0' + m : m;
  const sStr = s < 10 ? '0' + s : s;
  if (h > 0) return `${h}:${mStr}:${sStr}`;
  return `${mStr}:${sStr}`;
};

const formatTime12 = (date) => {
  if (!date || !(date instanceof Date) || isNaN(date)) return '--:--';
  return date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
};

const formatPartyCountdown = (ms) => {
  if (ms <= 0) return 'Starting...';
  const seconds = Math.floor(ms / 1000);
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}m ${s}s`;
};

// --- 3 HOUR OFFSET LOGIC (Moved outside component) ---
const getBlockStart = () => {
  const now = new Date();
  now.setUTCHours(Math.floor((now.getUTCHours() - 3) / 6) * 6 + 3, 0, 0, 0);
  return now.getTime();
};

let shakaPromise = null;
const loadShakaPlayer = () => {
  if (shakaPromise) return shakaPromise;
  shakaPromise = new Promise((resolve, reject) => {
    if (window.shaka) {
      resolve(window.shaka);
      return;
    }
    const script = document.createElement('script');
    script.src =
      'https://cdnjs.cloudflare.com/ajax/libs/shaka-player/4.3.5/shaka-player.ui.min.js';
    script.onload = () => resolve(window.shaka);
    script.onerror = reject;
    document.head.appendChild(script);
    const style = document.createElement('link');
    style.rel = 'stylesheet';
    style.href =
      'https://cdnjs.cloudflare.com/ajax/libs/shaka-player/4.3.5/controls.min.css';
    document.head.appendChild(style);
  });
  return shakaPromise;
};

// Helper to generate a 6-hour block
const generateBlock = (items, blockStartMs, startOffset = 0) => {
  const RADIO_BLOCK_DURATION = 6 * 60 * 60;
  const GAP_SECONDS = 10;
  const schedule = [];

  if (!items || !Array.isArray(items) || items.length === 0) return schedule;

  // 1. FILTERING: Use items created > 12h ago
  let pool = items.filter((item) => {
    if (!item.createdAt) return true;
    const created = new Date(item.createdAt).getTime();
    return blockStartMs - created >= 12 * 3600 * 1000;
  });

  // FALLBACK: Only if filtered pool is empty
  if (pool.length === 0) {
    pool = [...items];
  }

  if (pool.length === 0) return schedule;

  // Stable sort before shuffle
  pool.sort((a, b) =>
    (a.id || '').toString().localeCompare((b.id || '').toString())
  );

  const seedString = `BawsRadio_Block_${blockStartMs}`;
  const shuffled = shuffle(pool, seedString);

  let accumulatedTime = 0;
  let poolIndex = 0;

  while (accumulatedTime < RADIO_BLOCK_DURATION) {
    const item = shuffled[poolIndex % shuffled.length];
    const duration = parseDuration(item.duration) + 1;
    const safeDuration = duration > 0 ? duration : 180;

    if (
      accumulatedTime + safeDuration + GAP_SECONDS >
      RADIO_BLOCK_DURATION - 300
    )
      break;

    const nextItem = shuffled[(poolIndex + 1) % shuffled.length];

    schedule.push({
      ...item,
      type: 'content',
      playStartOffset: startOffset + accumulatedTime,
      playEndOffset: startOffset + accumulatedTime + safeDuration,
      uniqueKey: `${item.id}-${blockStartMs}-${accumulatedTime}`,
    });
    accumulatedTime += safeDuration;

    schedule.push({
      id: `countdown-${item.id}-${blockStartMs}-${accumulatedTime}`,
      type: 'countdown',
      title: `Up Next: ${nextItem.title}`,
      thumbnail: nextItem.thumbnail,
      duration: '00:10',
      playStartOffset: startOffset + accumulatedTime,
      playEndOffset: startOffset + accumulatedTime + GAP_SECONDS,
      uniqueKey: `countdown-${item.id}-${blockStartMs}-${accumulatedTime}`,
      nextItemTitle: nextItem.title,
      preloadSrc: nextItem.stream || nextItem.audio,
      preloadSubtitles: nextItem.subtitles,
    });
    accumulatedTime += GAP_SECONDS;
    poolIndex++;
  }

  const remainingTime = RADIO_BLOCK_DURATION - accumulatedTime;
  if (remainingTime > 0) {
    schedule.push({
      id: `station-break-${blockStartMs}`,
      type: 'content',
      title: 'Station Break • Music',
      thumbnail:
        'https://images.unsplash.com/photo-1478737270239-2f02b77ac6d5?auto=format&fit=crop&q=80&w=1000',
      audio: 'https://www.soundhelix.com/examples/mp3/SoundHelix-Song-1.mp3',
      duration: formatDurationString(remainingTime),
      playStartOffset: startOffset + accumulatedTime,
      playEndOffset: startOffset + RADIO_BLOCK_DURATION,
      uniqueKey: `station-break-${blockStartMs}`,
      isFiller: true,
    });
  }
  return schedule;
};

const generateRadioSchedule = (items) => {
  const RADIO_BLOCK_DURATION = 6 * 60 * 60;
  const now = new Date();
  const currentBlockStart = new Date(now);
  currentBlockStart.setUTCHours(
    Math.floor(currentBlockStart.getUTCHours() / 6) * 6,
    0,
    0,
    0
  );
  const blockStartMs = currentBlockStart.getTime();

  let schedule = generateBlock(items, blockStartMs, 0);

  const nowMs = now.getTime();
  const msIntoBlock = nowMs - blockStartMs;
  const threeHoursMs = 3 * 60 * 60 * 1000;

  if (msIntoBlock > threeHoursMs) {
    const nextBlockStartMs = blockStartMs + RADIO_BLOCK_DURATION * 1000;
    const nextBlockSchedule = generateBlock(
      items,
      nextBlockStartMs,
      RADIO_BLOCK_DURATION
    );
    schedule = [...schedule, ...nextBlockSchedule];
  }

  return { schedule, blockStartMs };
};

const ChapterTimeline = ({ chapters, duration, currentTime, onSeek }) => {
  if (!chapters || chapters.length === 0) return null;
  return (
    <div className="flex w-full h-1.5 mb-1 gap-0.5">
      {chapters.map((chapter, idx) => {
        const widthPercent =
          ((chapter.end - chapter.start) / (duration || 1)) * 100;
        const isActive =
          currentTime >= chapter.start && currentTime < chapter.end;
        return (
          <div
            key={idx}
            className={`h-full rounded-sm cursor-pointer transition-colors relative group ${isActive ? 'bg-blue-500' : 'bg-white/30 hover:bg-white/60'}`}
            style={{ width: `${widthPercent}%` }}
            onClick={(e) => {
              e.stopPropagation();
              onSeek(chapter.start);
            }}
          >
            <div className="absolute bottom-6 left-1/2 -translate-x-1/2 bg-black/90 text-xs text-white px-2 py-1 rounded opacity-0 group-hover:opacity-100 whitespace-nowrap pointer-events-none z-10 shadow-md border border-white/10">
              {chapter.title}
            </div>
          </div>
        );
      })}
    </div>
  );
};

// --- Mock Data (Updated keys) ---
const MOCK_DATA_RAW = [
  {
    speaker: 'Sujataa',
    dest: 'https://baws.in/books/baws/HI/Volume_40/pdf/20',
    title:
      'जो लोग करोड़ों लोगों को अछूत और अपराधी मानते हां उन्हें आजादी मांगने का हक नहीं - सोलापुर',
    description:
      'This speech addresses the issues of freedom and rights for the marginalized. \n\nRead more at https://baws.in/books/baws/HI/Volume_40 \n\nIt emphasizes that true freedom cannot exist where discrimination persists.',
    number: 233,
    date: '14-1-1946',
    place: 'सोलापुर',
    thumbnail:
      'https://baws-fd-endpoint-media-11cd090b-akdpeve3a7gef8gg.z03.azurefd.net/media/baws-audio/Sujataa/HI/Volume_40/thumbs/233.png',
    duration: '05:04',
    stream:
      'https://baws-fd-endpoint-media-11cd090b-akdpeve3a7gef8gg.z03.azurefd.net/media/baws-audio/Sujataa/HI/Volume_40/stream/233/stream.mpd',
    subtitles:
      'https://baws-fd-endpoint-media-11cd090b-akdpeve3a7gef8gg.z03.azurefd.net/media/baws-audio/Sujataa/HI/Volume_40/sbtl/233.srt',
    createdAt: '2023-01-01T12:00:00Z',
    chapters: [],
  },
  {
    speaker: 'Sujataa',
    dest: 'https://baws.in/books/baws/HI/Volume_40/pdf/22',
    title: 'क्षमतावान न भी हों तो चलेगा, लोग निष्ठावान होने चाहिएं - सोलापुर',
    description:
      'A powerful discourse on loyalty vs ability. Visit our archives: https://baws.in/ \n\nThe speaker argues that dedication to the cause is paramount.',
    number: 234,
    date: '14-1-1946',
    place: 'सोलापुर',
    thumbnail:
      'https://baws-fd-endpoint-media-11cd090b-akdpeve3a7gef8gg.z03.azurefd.net/media/baws-audio/Sujataa/HI/Volume_40/thumbs/234.png',
    duration: '13:03',
    stream:
      'https://baws-fd-endpoint-media-11cd090b-akdpeve3a7gef8gg.z03.azurefd.net/media/baws-audio/Sujataa/HI/Volume_40/stream/234/stream.mpd',
    subtitles:
      'https://baws-fd-endpoint-media-11cd090b-akdpeve3a7gef8gg.z03.azurefd.net/media/baws-audio/Sujataa/HI/Volume_40/sbtl/234.srt',
    createdAt: '2023-01-01T12:00:00Z',
    chapters: [],
  },
  {
    speaker: 'Sujataa',
    dest: 'https://baws.in/books/baws/HI/Volume_40/pdf/27',
    title:
      'स्वराज्य के लिए मेरा कतई विरोध नहीं, परंतु स्वराज्य में हमें भी हिस्सेदारी मिलनी चाहिए - दहिवडी (सातारा)',
    description:
      'On the topic of Swaraj and representation. \n\nKey points discussed:\n1. Representation\n2. Rights\n3. Governance',
    number: 235,
    date: '15-2-1946',
    place: 'दहिवडी (सातारा)',
    thumbnail:
      'https://baws-fd-endpoint-media-11cd090b-akdpeve3a7gef8gg.z03.azurefd.net/media/baws-audio/Sujataa/HI/Volume_40/thumbs/235.png',
    duration: '11:05',
    stream:
      'https://baws-fd-endpoint-media-11cd090b-akdpeve3a7gef8gg.z03.azurefd.net/media/baws-audio/Sujataa/HI/Volume_40/stream/235/stream.mpd',
    subtitles:
      'https://baws-fd-endpoint-media-11cd090b-akdpeve3a7gef8gg.z03.azurefd.net/media/baws-audio/Sujataa/HI/Volume_40/sbtl/235.srt',
    createdAt: '2023-01-01T12:00:00Z',
    chapters: [],
  },
];

// --- Sanghamitta Listen Together Modal ---
const SanghamittaModal = ({ isOpen, onClose, item }) => {
  const [partyTime, setPartyTime] = useState('');

  if (!isOpen || !item) return null;

  const copyInvitation = () => {
    if (!partyTime) {
      alert('Please select a time first.');
      return;
    }
    const date = new Date(partyTime);
    const utcTime = date.getTime();
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    // Generate Deep Link
    const url = `${window.location.origin}?item=${encodeURIComponent(item.id)}&party=${utcTime}`;

    let message = `🎧 Join me for a Listen Together party!\n\nTitle: ${item.title}`;

    // Add description if available
    if (item.description) {
      message += `\n\n${item.description}`;
    }

    message += `\n\nTime: ${date.toLocaleString()} (${timeZone})\n\nClick here to join: ${url}`;

    navigator.clipboard.writeText(message);
    alert('Invitation copied to clipboard!');
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
      <div className="bg-slate-900 border border-slate-700 rounded-2xl p-6 max-w-md w-full shadow-2xl">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            <Users className="text-blue-500" /> Sanghamitta
          </h2>
          <button onClick={onClose} className="text-slate-400 hover:text-white">
            <X size={20} />
          </button>
        </div>

        <p className="text-slate-400 text-sm mb-6">
          Schedule a "Listen Together" session for <strong>{item.title}</strong>
          . Share the link to play this track synchronously with friends.
        </p>

        <div className="mb-6">
          <label className="block text-xs font-bold text-slate-500 uppercase mb-2">
            Start Time (Your Timezone)
          </label>
          <input
            type="datetime-local"
            className="w-full bg-slate-800 border border-slate-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-blue-500"
            value={partyTime}
            onChange={(e) => setPartyTime(e.target.value)}
          />
        </div>

        <button
          onClick={copyInvitation}
          className="w-full bg-blue-600 hover:bg-blue-500 text-white font-bold py-3 rounded-lg flex items-center justify-center gap-2 transition-colors"
        >
          <Copy size={18} /> Copy Invitation
        </button>
      </div>
    </div>
  );
};

// --- Main Audio Player Component ---
const AudioPlayer = ({
  currentItem,
  isRadioMode,
  radioState,
  onNext,
  onManualEnded,
  shouldAutoPlay,
  history,
  saveHistory,
  toggleFavorite,
  isFavorite,
  onStartParty,
  isMini = false,
  onExpand,
  partySession,
  savedBookmarks = [],
  onAddBookmark,
  setRadioMuted, // Pass setter to handle fallback
}) => {
  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const loadedSrcRef = useRef(null);

  const [isPlaying, setIsPlaying] = useState(false);
  // We use prop for setting mute in App, but need local ref/state for player logic
  const [radioMuted, _setLocalRadioMuted] = useState(false);

  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(100);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [playerInstance, setPlayerInstance] = useState(null);
  const [isSubtitlesOn, setIsSubtitlesOn] = useState(false);
  const [countdownVal, setCountdownVal] = useState(10);

  // PARTY STATE
  const [partyCountdown, setPartyCountdown] = useState(null); // String or null

  // Controls Visibility State
  const [showControls, setShowControls] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  const onNextRef = useRef(onNext);
  const onManualEndedRef = useRef(onManualEnded);
  const saveHistoryRef = useRef(saveHistory);

  useEffect(() => {
    onNextRef.current = onNext;
  }, [onNext]);
  useEffect(() => {
    onManualEndedRef.current = onManualEnded;
  }, [onManualEnded]);
  useEffect(() => {
    saveHistoryRef.current = saveHistory;
  }, [saveHistory]);

  const isCountdown = currentItem.type === 'countdown';
  const loadIdRef = useRef(0);

  const isVisible =
    !isPlaying || isHovered || showControls || isCountdown || !!partyCountdown;

  // UPDATED: Sync Radio to Schedule with Fallback
  const syncRadioToSchedule = () => {
    if (!isRadioMode || !radioState) return;
    const nowMs = Date.now();
    const offsetInBlockSeconds = (nowMs - radioState.blockStartMs) / 1000;

    if (isCountdown) {
      if (typeof currentItem.playStartOffset !== 'number') return;
      const progress = offsetInBlockSeconds - currentItem.playStartOffset;
      const remaining = Math.max(0, Math.ceil(10 - progress));
      if (remaining !== countdownVal) {
        setCountdownVal(remaining);
        if (remaining > 0 && remaining <= 5) playBlip();
      }
      if (remaining <= 0) onNextRef.current();
      return;
    }

    if (videoRef.current) {
      if (typeof currentItem.playStartOffset !== 'number') return;
      const idealTime = offsetInBlockSeconds - currentItem.playStartOffset;
      if (Math.abs(videoRef.current.currentTime - idealTime) > 3) {
        if (
          idealTime >= 0 &&
          idealTime < (videoRef.current.duration || 10000)
        ) {
          videoRef.current.currentTime = idealTime;
        }
      }
      // UPDATED: Robust Autoplay
      if (videoRef.current.paused) {
        const playPromise = videoRef.current.play();
        if (playPromise !== undefined) {
          playPromise.catch((error) => {
            console.warn('Autoplay prevented. Attempting mute fallback.');
            // If play fails, mute and try again
            videoRef.current.muted = true;
            if (setRadioMuted) setRadioMuted(true); // Update parent state if provided
            _setLocalRadioMuted(true);
            videoRef.current
              .play()
              .catch((e) => console.error('Muted autoplay also failed', e));
          });
        }
      }
    }
  };

  // Sync Mute States independent of each other
  useEffect(() => {
    if (videoRef.current) {
      if (isRadioMode) {
        videoRef.current.muted = radioMuted;
      } else {
        videoRef.current.muted = false; // Always unmute manual player
      }
    }
  }, [isRadioMode, radioMuted]);

  // Force Unmute when switching TO Radio Mode
  useEffect(() => {
    if (isRadioMode) {
      _setLocalRadioMuted(false);
    }
  }, [isRadioMode]);

  useEffect(() => {
    if (!showControls) return;
    const t = setTimeout(() => setShowControls(false), 3500);
    return () => clearTimeout(t);
  }, [showControls]);

  const handleMouseEnter = () => setIsHovered(true);
  const handleMouseLeave = () => setIsHovered(false);

  useEffect(() => {
    const interval = setInterval(() => {
      if (
        !isRadioMode &&
        !isCountdown &&
        videoRef.current &&
        !videoRef.current.paused
      ) {
        const time = videoRef.current.currentTime;
        if (time > 0 && currentItem.id)
          saveHistoryRef.current(currentItem.id, time);
      }
    }, 5000);
    return () => clearInterval(interval);
  }, [isRadioMode, isCountdown, currentItem.id]);

  // --- PARTY TIMER LOGIC ---
  useEffect(() => {
    if (
      !partySession ||
      partySession.itemId !== currentItem.id ||
      isRadioMode
    ) {
      setPartyCountdown(null);
      return;
    }

    const checkPartyTime = () => {
      const now = Date.now();
      const diff = partySession.startTime - now;

      if (diff > 0) {
        // Future
        setPartyCountdown(formatPartyCountdown(diff));
        if (videoRef.current && !videoRef.current.paused)
          videoRef.current.pause();
      } else {
        // It's time!
        setPartyCountdown(null);
        if (videoRef.current) {
          // Handle late arrival
          const offset = Math.abs(diff) / 1000;
          if (offset < (videoRef.current.duration || 3600)) {
            if (Math.abs(videoRef.current.currentTime - offset) > 2) {
              videoRef.current.currentTime = offset;
            }
            if (videoRef.current.paused)
              videoRef.current.play().catch(() => {});
          }
        }
      }
    };

    const interval = setInterval(checkPartyTime, 1000);
    checkPartyTime(); // Initial check

    return () => clearInterval(interval);
  }, [partySession, currentItem.id, isRadioMode]);

  useEffect(() => {
    let player;
    loadShakaPlayer().then((shaka) => {
      shaka.polyfill.installAll();
      if (shaka.Player.isBrowserSupported() && videoRef.current) {
        player = new shaka.Player(videoRef.current);
        player.configure({
          textDisplayFactory: () =>
            new shaka.text.SimpleTextDisplayer(videoRef.current),
          abr: { enabled: true },
          streaming: { bufferingGoal: 30 }, // Aggressive buffering for seamless play
        });
        setPlayerInstance(player);
      }
    });
    return () => {
      if (player) player.destroy();
    };
  }, []);

  useEffect(() => {
    if (!playerInstance || !videoRef.current) return;
    const currentLoadId = ++loadIdRef.current;

    const src =
      currentItem.stream || currentItem.audio || currentItem.preloadSrc;
    const subSrc = currentItem.subtitles || currentItem.preloadSubtitles;

    if (!isCountdown) setIsSubtitlesOn(false);

    if (isCountdown && !src) {
      videoRef.current.pause();
      setCountdownVal(10);
      return;
    }

    // --- SEAMLESS TRANSITION LOGIC ---
    if (loadedSrcRef.current === src) {
      if (!isCountdown) {
        // Logic check: if party countdown is active, DO NOT autoplay
        const isPartyWaiting =
          partySession &&
          partySession.itemId === currentItem.id &&
          Date.now() < partySession.startTime;

        if (
          !isPartyWaiting &&
          (shouldAutoPlay || (isRadioMode && radioState))
        ) {
          const p = videoRef.current.play();
          if (p)
            p.catch(() => {
              // Fallback mute if normal autoplay fails here too
              videoRef.current.muted = true;
              _setLocalRadioMuted(true);
              videoRef.current.play().catch(() => {});
            });
        }
        if (subSrc) playerInstance.setTextTrackVisibility(isSubtitlesOn);
      } else {
        videoRef.current.pause();
        setCountdownVal(10);
      }
      return;
    }

    if (src) {
      loadedSrcRef.current = src;

      playerInstance
        .load(src)
        .then(async () => {
          if (currentLoadId !== loadIdRef.current) return;

          if (subSrc) {
            try {
              await playerInstance.addTextTrackAsync(
                subSrc,
                'hi',
                'subtitles',
                'text/srt'
              );
              playerInstance.setTextTrackVisibility(isSubtitlesOn);
            } catch (err) {
              console.error('Shaka Text Track Error:', err);
            }
          }

          if (isCountdown) {
            // PRELOAD MODE
            videoRef.current.pause();
            setCountdownVal(10);
          } else if (isRadioMode && radioState) {
            // RADIO MODE
            videoRef.current.muted = radioMuted;
            syncRadioToSchedule();
          } else {
            // MANUAL MODE
            // Check Party Status
            const isPartyWaiting =
              partySession &&
              partySession.itemId === currentItem.id &&
              Date.now() < partySession.startTime;

            if (isPartyWaiting) {
              // Load but wait
              videoRef.current.pause();
              videoRef.current.currentTime = 0;
            } else {
              // Resume Logic
              const savedMeta = getHistoryMeta(history, currentItem.id);
              const savedTime = savedMeta.progress;

              if (
                !partySession &&
                savedTime &&
                savedTime > 0 &&
                savedTime < (videoRef.current.duration || 3600)
              ) {
                videoRef.current.currentTime = savedTime;
              }

              // FIX: Allow autoplay if specifically requested OR if we are joining a party late
              if (
                shouldAutoPlay ||
                (partySession && partySession.itemId === currentItem.id)
              ) {
                videoRef.current.play().catch(() => {});
              } else {
                videoRef.current.pause();
                setIsPlaying(false);
              }
            }
          }
        })
        .catch((e) => {
          if (e.code !== 7000) console.error('Shaka Load Error', e);
        });
    }
  }, [
    playerInstance,
    currentItem.id,
    isRadioMode,
    isCountdown,
    shouldAutoPlay,
  ]);

  useEffect(() => {
    let interval;
    if (isRadioMode) {
      interval = setInterval(syncRadioToSchedule, 1000);
    } else if (isCountdown) {
      interval = setInterval(() => {
        setCountdownVal((prev) => {
          if (prev <= 1) {
            onNextRef.current();
            return 0;
          }
          if (prev <= 5) playBlip();
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [isRadioMode, currentItem, radioState, isCountdown]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || isCountdown) return;

    const updateTime = () => {
      setCurrentTime(video.currentTime);
      setDuration(video.duration || 100);
      setIsPlaying(!video.paused);
    };

    const handleEnded = () => {
      const timeoutId = setTimeout(() => {
        if (!isRadioMode) onManualEndedRef.current();
        else onNextRef.current();
      }, 1000);
    };

    video.addEventListener('timeupdate', updateTime);
    video.addEventListener('ended', handleEnded);
    return () => {
      video.removeEventListener('timeupdate', updateTime);
      video.removeEventListener('ended', handleEnded);
    };
  }, [isCountdown, isRadioMode]);

  const togglePlay = useCallback(
    (e) => {
      if (e) e.stopPropagation();
      if (isRadioMode) {
        // Toggle radio mute state
        // Update local and potentially trigger parent update (if prop exists, though we use local mostly)
        _setLocalRadioMuted((prev) => !prev);
      } else if (!isCountdown && videoRef.current) {
        videoRef.current.paused
          ? videoRef.current.play()
          : videoRef.current.pause();
      }
    },
    [isRadioMode, isCountdown]
  );

  const seek = (time) => {
    if (!isRadioMode && !isCountdown && videoRef.current)
      videoRef.current.currentTime = time;
  };
  const skip = (sec) =>
    seek(Math.min(Math.max(currentTime + sec, 0), duration));
  const changeSpeed = () => {
    const newRate =
      playbackRate === 1
        ? 1.25
        : playbackRate === 1.25
          ? 1.5
          : playbackRate === 1.5
            ? 2
            : 1;
    setPlaybackRate(newRate);
    if (videoRef.current) videoRef.current.playbackRate = newRate;
  };
  const toggleSubtitles = () => {
    if (playerInstance) {
      const newState = !isSubtitlesOn;
      setIsSubtitlesOn(newState);
      playerInstance.setTextTrackVisibility(newState);
    }
  };
  const copyShareLink = () => {
    const url = `${window.location.origin}?item=${encodeURIComponent(currentItem.id)}&t=${Math.floor(currentTime)}`;
    navigator.clipboard.writeText(url);
    alert(`Link copied: ${url}`);
  };
  const shareLast20s = () => {
    const start = Math.max(0, Math.floor(currentTime - 20));
    const url = `${window.location.origin}?item=${encodeURIComponent(currentItem.id)}&start=${start}&end=${Math.floor(currentTime)}`;
    navigator.clipboard.writeText(url);
    alert(`Clip copied: ${url}`);
  };
  const skipCountdown = () => {
    if (!isRadioMode && isCountdown) onNext();
  };
  const nextChapter = () => {
    const idx =
      currentItem.chapters?.findIndex(
        (c) => currentTime >= c.start && currentTime < c.end
      ) ?? -1;
    if (idx < (currentItem.chapters?.length || 0) - 1)
      seek(currentItem.chapters[idx + 1].start);
  };
  const prevChapter = () => {
    const idx =
      currentItem.chapters?.findIndex(
        (c) => currentTime >= c.start && currentTime < c.end
      ) ?? -1;
    if (idx > 0) seek(currentItem.chapters[idx - 1].start);
    else seek(0);
  };

  const toggleOverlay = useCallback(() => {
    setShowControls((prev) => !prev);
  }, []);

  const stopProp = (e) => e.stopPropagation();

  // --- Mini Player Render Logic ---
  if (isMini) {
    return (
      <div className="fixed bottom-0 left-0 right-0 bg-slate-900 border-t border-slate-800 p-2 flex items-center gap-4 shadow-2xl z-[100]">
        <video
          ref={videoRef}
          className="hidden"
          poster={currentItem.thumbnail}
        />

        <div
          className="w-12 h-12 rounded overflow-hidden flex-shrink-0 bg-slate-800 relative group cursor-pointer"
          onClick={onExpand}
        >
          <img
            src={currentItem.thumbnail}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
            <ChevronUp size={20} className="text-white" />
          </div>
        </div>
        <div className="flex-grow min-w-0 cursor-pointer" onClick={onExpand}>
          <h4 className="text-sm font-bold text-white truncate">
            {currentItem.title}
          </h4>
          <div className="flex items-center gap-2 text-xs text-slate-400">
            <span>
              {isRadioMode ? 'LIVE RADIO' : formatDurationString(currentTime)}
            </span>
            {!isRadioMode && (
              <div className="w-20 h-1 bg-slate-700 rounded-full overflow-hidden">
                <div
                  className="h-full bg-blue-500"
                  style={{ width: `${(currentTime / duration) * 100}%` }}
                ></div>
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-3 pr-2">
          <button
            onClick={togglePlay}
            className="w-10 h-10 rounded-full bg-white text-black flex items-center justify-center hover:bg-slate-200"
          >
            {isRadioMode ? (
              radioMuted ? (
                <VolumeX size={20} fill="currentColor" />
              ) : (
                <Volume2 size={20} fill="currentColor" />
              )
            ) : isPlaying ? (
              <Pause size={20} fill="currentColor" />
            ) : (
              <Play size={20} fill="currentColor" className="ml-0.5" />
            )}
          </button>
        </div>
      </div>
    );
  }

  // --- Full Player Render Logic ---
  return (
    <div className="bg-slate-900 rounded-xl p-0 md:p-0 shadow-2xl border border-slate-700 flex flex-col h-full overflow-hidden">
      <style>{shakaStyles}</style>

      {/* Main Video Container */}
      <div
        ref={containerRef}
        className="relative w-full bg-black group cursor-pointer aspect-video flex items-center justify-center overflow-hidden"
        onClick={toggleOverlay}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {/* IMPORTANT: crossOrigin="anonymous" enables CORS for tracks */}
        <video
          ref={videoRef}
          className="w-full h-full object-contain"
          poster={currentItem.thumbnail}
          crossOrigin="anonymous"
        >
          {/* Native Track Fallback for Subtitles */}
          {currentItem.subtitles && (
            <track
              label="Hindi"
              kind="subtitles"
              srcLang="hi"
              src={currentItem.subtitles}
              default={isSubtitlesOn}
            />
          )}
        </video>

        {isRadioMode && (
          <div className="absolute top-4 right-4 flex items-center gap-2 text-red-500 animate-pulse z-20 bg-black/50 px-2 py-1 rounded backdrop-blur pointer-events-none">
            <div className="w-2 h-2 bg-red-500 rounded-full"></div>
            <span className="text-xs font-bold tracking-widest uppercase">
              Live
            </span>
          </div>
        )}

        {/* MUTED RADIO OVERLAY - UPDATED: Click anywhere to unmute */}
        {isRadioMode && radioMuted && (
          <div
            className="absolute inset-0 z-[70] flex items-center justify-center bg-black/40 backdrop-blur-sm transition-opacity duration-300 cursor-pointer"
            onClick={(e) => {
              e.stopPropagation();
              _setLocalRadioMuted(false);
              if (videoRef.current) {
                videoRef.current.muted = false;
              }
            }}
          >
            <button className="group relative flex items-center gap-3 px-8 py-4 bg-white text-black rounded-full font-bold shadow-[0_0_30px_rgba(255,255,255,0.3)] hover:scale-105 transition-transform">
              <div className="absolute inset-0 rounded-full border-2 border-white animate-ping opacity-20"></div>
              <VolumeX size={24} className="text-red-500" />
              <span>Tap to Unmute Radio</span>
            </button>
          </div>
        )}

        {/* PARTY OVERLAY */}
        {partyCountdown && partySession && (
          <div
            className="absolute inset-0 z-50 bg-slate-900/90 backdrop-blur-md flex flex-col items-center justify-center text-center p-6 cursor-default"
            onClick={stopProp}
          >
            <div className="animate-pulse-ring rounded-full mb-6">
              <div className="w-20 h-20 bg-blue-600 rounded-full flex items-center justify-center shadow-xl">
                <Users size={40} className="text-white" />
              </div>
            </div>
            <h2 className="text-blue-400 text-sm font-bold uppercase tracking-widest mb-2">
              Listening Together Party
            </h2>
            <h1 className="text-white text-xl md:text-2xl font-bold mb-8 max-w-xl">
              {currentItem.title}
            </h1>

            <div className="grid grid-cols-2 gap-8 text-center mb-8">
              <div>
                <p className="text-slate-400 text-xs font-bold uppercase mb-1">
                  Starting At
                </p>
                <p className="text-white font-mono text-lg">
                  {new Date(partySession.startTime).toLocaleTimeString()}
                </p>
              </div>
              <div>
                <p className="text-slate-400 text-xs font-bold uppercase mb-1">
                  In
                </p>
                <p className="text-white font-mono text-lg">{partyCountdown}</p>
              </div>
            </div>
            <p className="text-slate-500 text-xs animate-pulse">
              Waiting for host to sync...
            </p>
          </div>
        )}

        {isCountdown && (
          <div
            className="absolute inset-0 z-50 bg-slate-900/95 flex flex-col items-center justify-center text-center backdrop-blur-sm p-4 cursor-default"
            onClick={stopProp}
          >
            {!isRadioMode && (
              <button
                onClick={skipCountdown}
                className="absolute top-4 right-4 flex items-center gap-1 text-slate-400 hover:text-white bg-slate-800 px-3 py-1 rounded-full text-xs font-bold"
              >
                Skip <FastForward size={12} />
              </button>
            )}
            <h2 className="text-slate-400 text-xs uppercase tracking-widest font-bold mb-1">
              Up Next
            </h2>
            <h1 className="text-lg md:text-xl font-bold text-white mb-4 line-clamp-2 px-4">
              {currentItem.nextItemTitle}
            </h1>
            <div className="relative w-24 h-24 mx-auto flex items-center justify-center">
              <svg
                className="absolute inset-0 w-full h-full"
                viewBox="0 0 100 100"
              >
                <circle
                  cx="50"
                  cy="50"
                  r="45"
                  fill="none"
                  stroke="#1e293b"
                  strokeWidth="8"
                />
                <circle
                  cx="50"
                  cy="50"
                  r="45"
                  fill="none"
                  stroke="#3b82f6"
                  strokeWidth="8"
                  strokeDasharray="280"
                  strokeDashoffset={280 - (280 * (10 - countdownVal)) / 10}
                  className="transition-all duration-1000 ease-linear"
                  strokeLinecap="round"
                />
              </svg>
              <span className="text-4xl font-black text-white font-mono tabular-nums">
                {countdownVal}
              </span>
            </div>
          </div>
        )}

        {/* Controls Overlay */}
        <div
          className={`absolute inset-0 bg-gradient-to-t from-black/90 via-transparent to-transparent flex flex-col justify-end p-4 transition-opacity duration-300 z-[60] ${isVisible ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}
        >
          {/* Top-Right Controls (Speed, Subs) */}
          <div className="absolute top-4 right-4 flex gap-2" onClick={stopProp}>
            <button
              onClick={(e) => {
                e.stopPropagation();
                changeSpeed();
              }}
              disabled={isRadioMode || isCountdown || partyCountdown}
              className="px-2 py-1 bg-black/50 hover:bg-black/70 rounded text-xs font-bold text-white border border-white/20 backdrop-blur-md"
            >
              {playbackRate}x
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                toggleSubtitles();
              }}
              disabled={isCountdown}
              className={`p-1.5 rounded bg-black/50 hover:bg-black/70 border border-white/20 backdrop-blur-md ${isSubtitlesOn ? 'text-blue-400' : 'text-white'}`}
            >
              <Captions size={16} />
            </button>
          </div>

          {/* Bottom Controls Wrapper - Added stopProp to prevent closing when using controls */}
          <div className="w-full" onClick={stopProp}>
            {/* Timeline Section */}
            <div className="mb-2 group/timeline relative">
              <ChapterTimeline
                chapters={currentItem.chapters}
                duration={duration}
                currentTime={currentTime}
                onSeek={seek}
              />
              <div className="relative h-3 flex items-center">
                <input
                  type="range"
                  min="0"
                  max={duration || 100}
                  value={currentTime}
                  onChange={(e) => seek(Number(e.target.value))}
                  disabled={isRadioMode || isCountdown || partyCountdown}
                  className={`absolute w-full h-1 bg-white/30 rounded-lg appearance-none cursor-pointer accent-blue-500 hover:h-1.5 transition-all z-20 ${isRadioMode || isCountdown || partyCountdown ? 'cursor-not-allowed opacity-50' : ''}`}
                />
                <div
                  className="absolute h-1 bg-blue-500 rounded-lg z-10 pointer-events-none"
                  style={{ width: `${(currentTime / duration) * 100}%` }}
                />

                {/* CHANGED: Map over savedBookmarks array to render multiple markers */}
                {savedBookmarks &&
                  savedBookmarks.map((bmTime, index) => (
                    <div
                      key={index}
                      className="absolute w-3 h-3 bg-yellow-400 border border-black rounded-full z-30 cursor-pointer hover:scale-150 transition-transform shadow-lg"
                      style={{
                        left: `${(bmTime / (duration || 1)) * 100}%`,
                        marginLeft: '-6px',
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        seek(bmTime);
                      }}
                      title={`Jump to bookmark: ${formatDurationString(bmTime)}`}
                    />
                  ))}
              </div>
              <div className="flex justify-between text-xs font-medium text-slate-300 mt-1 tabular-nums">
                <span>{formatDurationString(currentTime)}</span>
                <span>{formatDurationString(duration)}</span>
              </div>
            </div>

            {/* Main Controls Row */}
            <div className="flex items-center justify-between">
              {/* Left Actions */}
              <div className="flex items-center gap-4">
                <button
                  onClick={prevChapter}
                  disabled={isRadioMode || isCountdown || partyCountdown}
                  className="hidden md:flex text-slate-300 hover:text-white disabled:opacity-30"
                >
                  <SkipBack size={20} />
                </button>
                <button
                  onClick={() => skip(-10)}
                  disabled={isRadioMode || isCountdown || partyCountdown}
                  className="text-slate-300 hover:text-white disabled:opacity-30"
                >
                  <Rewind size={24} />
                </button>

                <button
                  onClick={togglePlay}
                  disabled={(isRadioMode && isCountdown) || partyCountdown}
                  className="w-12 h-12 flex items-center justify-center rounded-full bg-white text-black hover:bg-slate-200 transition-transform hover:scale-105 active:scale-95 disabled:opacity-50"
                >
                  {isRadioMode ? (
                    radioMuted ? (
                      <VolumeX size={24} fill="currentColor" />
                    ) : (
                      <Volume2 size={24} fill="currentColor" />
                    )
                  ) : isPlaying && !isCountdown && !partyCountdown ? (
                    <Pause size={24} fill="currentColor" />
                  ) : (
                    <Play size={24} fill="currentColor" className="ml-0.5" />
                  )}
                </button>

                <button
                  onClick={() => skip(10)}
                  disabled={isRadioMode || isCountdown || partyCountdown}
                  className="text-slate-300 hover:text-white disabled:opacity-30"
                >
                  <FastForward size={24} />
                </button>
                <button
                  onClick={nextChapter}
                  disabled={isRadioMode || isCountdown || partyCountdown}
                  className="hidden md:flex text-slate-300 hover:text-white disabled:opacity-30"
                >
                  <SkipForward size={20} />
                </button>
              </div>

              {/* Right Actions */}
              <div className="flex items-center gap-4">
                <button
                  onClick={() => {
                    const bookmarkTime = Math.max(0, currentTime - 20);
                    onAddBookmark(currentItem.id, bookmarkTime);
                    const url = `${window.location.origin}?item=${encodeURIComponent(currentItem.id)}&t=${Math.floor(bookmarkTime)}`;
                    navigator.clipboard.writeText(url);
                    alert(
                      `Bookmark added at ${formatDurationString(bookmarkTime)} & Link copied!`
                    );
                  }}
                  className={`${savedBookmarks.length > 0 ? 'text-yellow-400' : 'text-slate-300'} hover:text-yellow-300 disabled:opacity-30`}
                  // CHANGED: Removed isRadioMode from disabled condition so users can bookmark while listening to radio
                  disabled={isCountdown || partyCountdown}
                  title="Add Bookmark (T-20s) & Copy Link"
                >
                  <Bookmark
                    size={20}
                    fill={savedBookmarks.length > 0 ? 'currentColor' : 'none'}
                  />
                </button>
                <button
                  onClick={copyShareLink}
                  className="text-slate-300 hover:text-blue-400"
                >
                  <Share2 size={20} />
                </button>
                {/* Favorites & Party Buttons in Player */}
                <button
                  onClick={() => toggleFavorite(currentItem.id)}
                  className={`${isFavorite ? 'text-yellow-400' : 'text-slate-300'} hover:text-yellow-300`}
                >
                  <Star size={20} fill={isFavorite ? 'currentColor' : 'none'} />
                </button>
                <button
                  onClick={() => onStartParty(currentItem)}
                  className="text-slate-300 hover:text-green-400"
                >
                  <Users size={20} />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Metadata Section (Outside Video) */}
      <div className="p-4 md:p-6 bg-slate-900 border-t border-slate-800">
        <h1 className="text-lg md:text-xl font-bold text-slate-100 leading-snug mb-2 line-clamp-2">
          {currentItem.title}
        </h1>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-xs md:text-sm text-slate-400">
          {!isCountdown && (
            <span className="bg-slate-800 px-2 py-0.5 rounded border border-slate-700">
              #{currentItem.number}
            </span>
          )}
          <span className="flex items-center gap-1">
            <Clock size={14} /> {currentItem.date}
          </span>
          <span className="flex items-center gap-1">
            <MapPin size={14} /> {currentItem.place}
          </span>
          {currentItem.dest && (
            <a
              href={currentItem.dest}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1 text-blue-400 hover:text-blue-300 hover:underline"
            >
              <FileText size={14} /> Source Text
            </a>
          )}
        </div>
      </div>
    </div>
  );
};

// --- QueueCarousel (Optimized) ---
const QueueCarousel = ({
  queue,
  currentId,
  onPlay,
  onRemove,
  onReorder,
  onClear,
  onSave,
  isRadioMode,
  radioState,
  history,
}) => {
  const displayQueue =
    isRadioMode && radioState
      ? radioState.schedule.filter((i) => i.type !== 'countdown')
      : queue;

  // --- Refactored Queue Carousel Logic ---
  // 1. "Outside" Control Bar logic
  // 2. Touch/Click-safe reordering (no hover required)
  // 3. "Edit Mode" via long-press for delete

  const [editMode, setEditMode] = useState(false);
  const longPressTimer = useRef(null);
  const queueRef = useRef(null);

  // Click outside listener to exit edit mode
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (queueRef.current && !queueRef.current.contains(e.target)) {
        setEditMode(false);
      }
    };
    document.addEventListener('touchstart', handleClickOutside);
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('touchstart', handleClickOutside);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleTouchStart = () => {
    longPressTimer.current = setTimeout(() => {
      setEditMode(true);
      if (navigator.vibrate) navigator.vibrate(50);
    }, 800);
  };

  const handleTouchEnd = () => {
    if (longPressTimer.current) clearTimeout(longPressTimer.current);
  };

  const now = Date.now();
  if (displayQueue.length === 0)
    return (
      <div className="bg-slate-900 border border-slate-800 rounded-xl p-6 flex justify-center items-center text-slate-500 h-40">
        <List size={24} className="mb-2 opacity-50" />
        <span className="text-sm">Queue is empty</span>
      </div>
    );

  return (
    <div
      ref={queueRef}
      className="bg-slate-900 border border-slate-700 rounded-xl overflow-hidden flex flex-col"
    >
      <div className="px-4 py-3 border-b border-slate-800 flex justify-between items-center bg-slate-900/50">
        <h3 className="text-slate-100 font-semibold text-sm flex items-center gap-2">
          {isRadioMode ? (
            <>
              <span className="text-red-400 animate-pulse font-bold">
                ● LIVE Schedule
              </span>
            </>
          ) : (
            <>
              <Radio size={16} className="text-blue-500" /> Up Next
            </>
          )}
        </h3>
        {!isRadioMode && (
          <div className="flex gap-2">
            <button
              onClick={() => setEditMode(!editMode)}
              className={`text-xs font-medium ${editMode ? 'text-red-400' : 'text-slate-400'}`}
            >
              {editMode ? 'Done' : 'Edit'}
            </button>
            <button
              onClick={onClear}
              className="text-xs text-red-400 font-medium"
            >
              Clear
            </button>
          </div>
        )}
      </div>
      <div className="p-4 flex gap-3 overflow-x-auto">
        {displayQueue.map((item) => {
          const isActive = isRadioMode
            ? item.uniqueKey === currentId
            : item.id === currentId;
          if (
            isRadioMode &&
            radioState &&
            radioState.blockStartMs + item.playEndOffset * 1000 < now - 60000
          )
            return null;
          let badgeText = null;
          if (isRadioMode && radioState) {
            if (isActive) badgeText = 'ON AIR';
            else
              badgeText = formatTime12(
                new Date(radioState.blockStartMs + item.playStartOffset * 1000)
              );
          }
          // CHANGED: Fixed history lookup key
          const saved = getHistoryMeta(history, item.id).progress;
          const total = parseDuration(item.duration);
          const progressPct = total > 0 ? (saved / total) * 100 : 0;

          return (
            <div
              key={isRadioMode ? item.uniqueKey : item.id}
              className={`flex-shrink-0 w-40 p-2 rounded-lg border transition-all group relative ${isActive ? 'bg-slate-800 border-blue-500/50 shadow-lg' : 'bg-slate-800/50 border-slate-800'}`}
              onTouchStart={!isRadioMode ? handleTouchStart : undefined}
              onTouchEnd={!isRadioMode ? handleTouchEnd : undefined}
              onMouseDown={!isRadioMode ? handleTouchStart : undefined}
              onMouseUp={!isRadioMode ? handleTouchEnd : undefined}
            >
              <div
                className="aspect-video w-full bg-slate-700 rounded mb-2 overflow-hidden cursor-pointer relative"
                onClick={() => !isRadioMode && onPlay(item)}
              >
                <img
                  src={item.thumbnail}
                  alt={item.title}
                  className={`w-full h-full object-cover opacity-80 transition-all ${editMode ? 'animate-jiggle' : ''}`}
                />

                {/* Edit Mode: Big X Overlay */}
                {editMode && !isRadioMode && (
                  <div
                    className="absolute inset-0 bg-black/60 flex items-center justify-center z-30"
                    onClick={(e) => {
                      e.stopPropagation();
                      onRemove(item.id);
                    }}
                  >
                    <Trash2 size={32} className="text-red-500 drop-shadow-lg" />
                  </div>
                )}

                {isActive && (
                  <div className="absolute inset-0 flex items-center justify-center bg-black/40">
                    <div className="w-2 h-2 md:w-3 md:h-3 rounded-full bg-blue-500 animate-pulse" />
                  </div>
                )}
                {isRadioMode && badgeText && (
                  <div
                    className={`absolute top-1 left-1 px-1.5 py-0.5 rounded text-[10px] font-bold z-10 backdrop-blur-md border ${badgeText === 'ON AIR' ? 'bg-red-600/80 text-white border-red-500' : 'bg-black/60 text-slate-300 border-white/10'}`}
                  >
                    {badgeText}
                  </div>
                )}
                <div className="absolute bottom-0 left-0 right-0 h-1 bg-white/20">
                  <div
                    className="h-full bg-red-500"
                    style={{ width: `${Math.min(progressPct, 100)}%` }}
                  />
                </div>
              </div>

              <h4 className="text-xs font-medium text-slate-200 truncate leading-tight mb-1">
                {item.title}
              </h4>
              <div className="text-[10px] text-slate-500 mb-1">
                {item.duration}
              </div>

              {/* NEW: Control Bar Below Info */}
              {!isRadioMode && (
                <div className="flex items-center justify-between bg-slate-900/50 rounded px-1 py-1">
                  <div className="flex gap-1">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onReorder(item.id, -1);
                      }}
                      className="p-1 hover:bg-slate-700 rounded text-slate-400 hover:text-white"
                    >
                      <ChevronLeft size={14} />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onReorder(item.id, 1);
                      }}
                      className="p-1 hover:bg-slate-700 rounded text-slate-400 hover:text-white"
                    >
                      <ChevronRight size={14} />
                    </button>
                  </div>
                  {/* Optional Small Trash Icon if not in Edit Mode */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onRemove(item.id);
                    }}
                    className="p-1 hover:bg-slate-700 rounded text-slate-500 hover:text-red-400"
                  >
                    <X size={14} />
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// --- Archive Item Component ---
const ArchiveListItem = ({
  item,
  onPlay,
  onQueue,
  onRemove,
  isInQueue,
  saved,
  total,
  toggleFavorite,
  isFavorite,
  onStartParty,
}) => {
  const [expanded, setExpanded] = useState(false);
  const progressPct = total > 0 ? (saved / total) * 100 : 0;

  return (
    <div className="group p-4 hover:bg-slate-800/50 transition-colors flex flex-col md:flex-row gap-4 items-start md:items-start border-b border-slate-800 last:border-0">
      {/* Thumbnail Section */}
      <div className="relative w-full md:w-40 aspect-video bg-slate-800 rounded-lg overflow-hidden flex-shrink-0">
        <picture>
          {/* Mobile: use _s.png */}
          <source
            media="(max-width: 767px)"
            srcSet={item.thumbnail?.replace('.png', '_s.png')}
          />
          {/* Desktop: use _m.png */}
          <source
            media="(min-width: 768px)"
            srcSet={item.thumbnail?.replace('.png', '_m.png')}
          />
          {/* Fallback & Attributes */}
          <img
            src={item.thumbnail?.replace('.png', '_m.png')}
            loading="lazy"
            className="w-full h-full object-cover opacity-80"
            alt={item.title}
          />
        </picture>

        <div
          className="absolute inset-0 flex items-center justify-center bg-black/20 group-hover:bg-black/0 transition-colors cursor-pointer"
          onClick={() => onPlay(item)}
        >
          <Play
            size={32}
            className="text-white opacity-0 group-hover:opacity-100 drop-shadow-md"
          />
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-white/20">
          <div
            className="h-full bg-red-500"
            style={{ width: `${Math.min(progressPct, 100)}%` }}
          />
        </div>
        <div className="absolute bottom-1 right-1 bg-black/80 px-1.5 py-0.5 text-[10px] text-white rounded font-mono">
          {item.duration}
        </div>
      </div>

      {/* Info Section */}
      <div className="flex-grow min-w-0 w-full">
        <div className="flex justify-between items-start gap-4">
          <h3
            className="text-slate-200 font-medium text-base leading-tight mb-1 cursor-pointer hover:text-blue-400"
            onClick={() => onPlay(item)}
          >
            {item.title}
          </h3>
        </div>

        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-500 mb-2">
          <span className="flex items-center gap-1">
            <MapPin size={12} /> {item.place}
          </span>
          <span className="flex items-center gap-1">
            <Clock size={12} /> {item.date}
          </span>
        </div>

        {/* Expandable Description */}
        {item.description && (
          <div className="text-sm text-slate-400 mb-3">
            <div
              className={`relative ${expanded ? '' : 'max-h-[4.5em] overflow-y-auto'}`}
            >
              <div className="whitespace-pre-wrap leading-relaxed">
                <Linkify text={item.description} />
              </div>
              {/* Fade out effect when collapsed - Hidden if text is short or expanded */}
              {/* {!expanded && <div className="absolute bottom-0 left-0 right-0 h-6 bg-gradient-to-t from-slate-900 to-transparent pointer-events-none md:from-transparent md:via-slate-800/0" />} */}
            </div>
            <button
              onClick={() => setExpanded(!expanded)}
              className="text-xs font-medium text-blue-400 hover:text-blue-300 mt-1 flex items-center gap-1"
            >
              {expanded ? 'Show Less' : 'Show More'}{' '}
              <ChevronDown
                size={12}
                className={`transition-transform ${expanded ? 'rotate-180' : ''}`}
              />
            </button>
          </div>
        )}
      </div>

      {/* Actions Section */}
      <div className="flex items-center gap-2 w-full md:w-auto mt-auto">
        <button
          onClick={() => onPlay(item)}
          className="flex-1 md:flex-none flex items-center justify-center gap-2 px-4 py-2 bg-slate-800 hover:bg-blue-600 text-slate-300 hover:text-white rounded-lg border border-slate-700 hover:border-blue-500 transition-all text-sm font-medium group/btn"
        >
          <Play size={16} /> <span className="md:hidden lg:inline">Play</span>
        </button>
        <button
          onClick={() => (isInQueue ? onRemove(item.id) : onQueue(item))}
          className={`flex-1 md:flex-none flex items-center justify-center gap-2 px-4 py-2 rounded-lg border transition-all text-sm font-medium group/btn ${
            isInQueue
              ? 'bg-blue-600 text-white border-blue-500 hover:bg-red-600 hover:border-red-500'
              : 'bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white border-slate-700'
          }`}
        >
          {isInQueue ? (
            <Check size={16} className="group-hover/btn:hidden" />
          ) : (
            <List size={16} />
          )}
          {isInQueue && (
            <X size={16} className="hidden group-hover/btn:block" />
          )}
          <span className="md:hidden lg:inline">
            {isInQueue ? (
              <span className="group-hover/btn:hidden">Queued</span>
            ) : (
              'Queue'
            )}
            {isInQueue && (
              <span className="hidden group-hover/btn:inline">Remove</span>
            )}
          </span>
        </button>

        {/* Star Button */}
        <button
          onClick={() => toggleFavorite(item.id)}
          className={`p-2 rounded-lg border border-slate-700 hover:bg-slate-800 ${isFavorite ? 'text-yellow-400' : 'text-slate-400'}`}
        >
          <Star size={16} fill={isFavorite ? 'currentColor' : 'none'} />
        </button>

        {/* Party Button */}
        <button
          onClick={() => onStartParty(item)}
          className="p-2 rounded-lg border border-slate-700 hover:bg-slate-800 text-slate-400 hover:text-green-400"
        >
          <Users size={16} />
        </button>
      </div>
    </div>
  );
};

// --- ArchiveList Wrapper ---
const ArchiveList = ({
  items,
  onPlay,
  onQueue,
  onRemove,
  queue,
  history,
  toggleFavorite,
  favorites,
  onStartParty,
}) => {
  const [search, setSearch] = useState('');
  const [filterPlace, setFilterPlace] = useState('');
  const [filterYear, setFilterYear] = useState('');

  const years = useMemo(() => {
    const y = items
      .map((i) => (i.date ? i.date.split('-').pop() : ''))
      .filter((y) => y && y.length === 4);
    return [...new Set(y)].sort();
  }, [items]);

  // UPDATED: Sort by recency
  const filteredItems = useMemo(() => {
    // First, filter
    let result = items.filter((item) => {
      const itemYear = item.date ? item.date.split('-').pop() : '';
      return (
        item.title.toLowerCase().includes(search.toLowerCase()) &&
        (filterPlace ? item.place === filterPlace : true) &&
        (filterYear ? itemYear === filterYear : true)
      );
    });

    // Then sort by last played time (descending)
    result.sort((a, b) => {
      const lastPlayedA = getHistoryMeta(history, a.id).lastPlayed;
      const lastPlayedB = getHistoryMeta(history, b.id).lastPlayed;
      // Sort descending: most recent first
      return lastPlayedB - lastPlayedA;
    });

    return result;
  }, [items, search, filterPlace, filterYear, history]); // Added history dependency

  const places = useMemo(
    () => [...new Set(items.map((i) => i.place).filter(Boolean))],
    [items]
  );

  return (
    <div className="bg-slate-900 border border-slate-700 rounded-xl overflow-hidden mt-6">
      {/* UPDATED: Changed grid to flex on mobile to force single line horizontal scroll */}
      <div className="p-4 border-b border-slate-800 flex md:grid md:grid-cols-12 gap-4 overflow-x-auto md:overflow-visible no-scrollbar snap-x items-center">
        {/* Added flex-shrink-0 to prevent items from squishing on mobile */}
        <div className="min-w-[250px] md:min-w-0 md:col-span-6 relative snap-start flex-shrink-0">
          <Search
            className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500"
            size={18}
          />
          <input
            type="text"
            placeholder="Search titles..."
            className="w-full bg-slate-950 border border-slate-800 text-slate-200 pl-10 pr-4 py-2 rounded-lg focus:outline-none focus:border-blue-500 text-sm"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="min-w-[150px] md:min-w-0 md:col-span-3 snap-start flex-shrink-0">
          <select
            className="w-full bg-slate-950 border border-slate-800 text-slate-200 px-3 py-2 rounded-lg focus:outline-none focus:border-blue-500 text-sm"
            value={filterPlace}
            onChange={(e) => setFilterPlace(e.target.value)}
          >
            <option value="">All Places</option>
            {places.map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </select>
        </div>
        <div className="min-w-[150px] md:min-w-0 md:col-span-3 snap-start flex-shrink-0">
          <select
            className="w-full bg-slate-950 border border-slate-800 text-slate-200 px-3 py-2 rounded-lg focus:outline-none focus:border-blue-500 text-sm"
            value={filterYear}
            onChange={(e) => setFilterYear(e.target.value)}
          >
            <option value="">All Years</option>
            {years.map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div className="divide-y divide-slate-800 max-h-[600px] overflow-y-auto">
        {filteredItems.map((item) => (
          <ArchiveListItem
            key={item.id}
            item={item}
            onPlay={onPlay}
            onQueue={onQueue}
            onRemove={onRemove}
            isInQueue={queue.some((q) => q.id === item.id)}
            // Updated to use helper
            saved={getHistoryMeta(history, item.id).progress}
            total={parseDuration(item.duration)}
            toggleFavorite={toggleFavorite}
            isFavorite={favorites.includes(item.id)}
            onStartParty={onStartParty}
          />
        ))}
      </div>
    </div>
  );
};

export default function AudioPlayerPage() {
  const [items, setItems] = useState([]);
  const [manualQueue, setManualQueue] = useState(() => {
    try {
      const stored = localStorage.getItem(QUEUE_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch (e) {
      return [];
    }
  });
  const [currentManualItem, setCurrentManualItem] = useState(null);
  const [isRadioMode, setIsRadioMode] = useState(false);
  const [currentRadioItem, setCurrentRadioItem] = useState(null);
  const [shouldAutoPlay, setShouldAutoPlay] = useState(false);
  const [view, setView] = useState('player');

  const [favorites, setFavorites] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(FAV_KEY) || '[]');
    } catch (e) {
      return [];
    }
  });

  const [bookmarks, setBookmarks] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(BOOKMARK_KEY) || '{}');
    } catch (e) {
      return {};
    }
  });

  const [partyItem, setPartyItem] = useState(null);
  const [partySession, setPartySession] = useState(null);

  const [history, setHistory] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(HISTORY_KEY) || '{}');
    } catch (e) {
      return {};
    }
  });

  const [blockStartMs, setBlockStartMs] = useState(getBlockStart());

  const toggleFavorite = useCallback((id) => {
    setFavorites((prev) => {
      const next = prev.includes(id)
        ? prev.filter((i) => i !== id)
        : [...prev, id];
      localStorage.setItem(FAV_KEY, JSON.stringify(next));
      return next;
    });
  }, []);

  const handleAddBookmark = useCallback((id, time) => {
    setBookmarks((prev) => {
      const current = Array.isArray(prev[id])
        ? prev[id]
        : prev[id] !== undefined
          ? [prev[id]]
          : [];
      const nextList = [...current, time].sort((a, b) => a - b);

      const nextState = { ...prev, [id]: nextList };
      localStorage.setItem(BOOKMARK_KEY, JSON.stringify(nextState));
      return nextState;
    });
  }, []);

  // UPDATED: Save history with timestamp for recency sorting
  const saveHistory = useCallback((key, time) => {
    setHistory((prev) => {
      const next = {
        ...prev,
        [key]: {
          progress: time,
          lastPlayed: Date.now(),
        },
      };
      localStorage.setItem(HISTORY_KEY, JSON.stringify(next));
      return next;
    });
  }, []);

  const handleManualEnded = useCallback(() => {
    const idx = manualQueue.findIndex((i) => i.id === currentManualItem?.id);
    let nextItem = null;
    if (idx >= 0 && idx < manualQueue.length - 1)
      nextItem = manualQueue[idx + 1];
    else if (manualQueue.length) nextItem = manualQueue[0];

    if (nextItem) {
      setShouldAutoPlay(true);
      setCurrentManualItem({
        id: `countdown-manual-${Date.now()}`,
        type: 'countdown',
        title: `Up Next: ${nextItem.title}`,
        thumbnail: nextItem.thumbnail,
        duration: '00:10',
        nextItemTitle: nextItem.title,
        actualNextItem: nextItem,
        preloadSrc: nextItem.stream || nextItem.audio,
        preloadSubtitles: nextItem.subtitles,
      });
    }
  }, [currentManualItem, manualQueue]);

  const advanceManualTrack = useCallback(() => {
    if (currentManualItem?.actualNextItem) {
      setCurrentManualItem(currentManualItem.actualNextItem);
    } else {
      const idx = manualQueue.findIndex((i) => i.id === currentManualItem?.id);
      if (manualQueue.length > 0)
        setCurrentManualItem(manualQueue[(idx + 1) % manualQueue.length]);
    }
    setShouldAutoPlay(true);
  }, [currentManualItem, manualQueue]);

  useEffect(() => {
    localStorage.setItem(QUEUE_KEY, JSON.stringify(manualQueue));
  }, [manualQueue]);

  useEffect(() => {
    const interval = setInterval(() => {
      const newBlockStart = getBlockStart();
      if (newBlockStart !== blockStartMs) setBlockStartMs(newBlockStart);
    }, 60000);
    return () => clearInterval(interval);
  }, [blockStartMs]);

  const radioState = useMemo(
    () => generateRadioSchedule(items),
    [items, blockStartMs]
  );

  const handleReorder = (id, direction) => {
    const index = manualQueue.findIndex((i) => i.id === id);
    if (index < 0) return;
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= manualQueue.length) return;

    const newQueue = [...manualQueue];
    const [item] = newQueue.splice(index, 1);
    newQueue.splice(newIndex, 0, item);
    setManualQueue(newQueue);
  };

  // --- SAFETY EFFECT FOR EMPTY STATE ---
  useEffect(() => {
    // If items loaded, not in radio mode, and no manual item selected, force selection
    if (items.length > 0 && !currentManualItem && !isRadioMode) {
      // Try to find most recent from history
      let mostRecentItem = null;
      let maxTimestamp = 0;

      items.forEach((item) => {
        const meta = getHistoryMeta(history, item.id);
        if (meta.lastPlayed > maxTimestamp) {
          maxTimestamp = meta.lastPlayed;
          mostRecentItem = item;
        }
      });

      // Fallback to first item if no history
      setCurrentManualItem(mostRecentItem || items[0]);
    }
  }, [items, currentManualItem, isRadioMode, history]);

  // --- WINDOW TITLE LOGIC ---
  useEffect(() => {
    if (isRadioMode && currentRadioItem) {
      // Use nextItemTitle if available (during countdown) to avoid "Up Next:" prefix in window title
      const displayTitle =
        currentRadioItem.nextItemTitle || currentRadioItem.title;
      document.title = `Radio ${displayTitle}`;
    } else {
      if (currentManualItem) {
        // Use nextItemTitle if available (during countdown)
        const displayTitle =
          currentManualItem.nextItemTitle || currentManualItem.title;

        if (partySession && partySession.itemId === currentManualItem.id) {
          document.title = `Group Listening ${displayTitle}`;
        } else {
          document.title = `Listening ${displayTitle}`;
        }
      }
    }
  }, [isRadioMode, currentRadioItem, currentManualItem, partySession]);

  // --- URL SYNC EFFECT ---
  useEffect(() => {
    if (items.length === 0) return;

    const params = new URLSearchParams(window.location.search);
    let updated = false;

    if (isRadioMode) {
      if (params.get('radio') !== 'true') {
        params.set('radio', 'true');
        params.delete('item');
        params.delete('t');
        params.delete('party');
        updated = true;
      }
    } else {
      if (params.has('radio')) {
        params.delete('radio');
        updated = true;
      }

      if (currentManualItem && currentManualItem.id) {
        if (params.get('item') !== currentManualItem.id) {
          params.set('item', currentManualItem.id);
          params.delete('t');
          updated = true;
        }
      }

      if (partySession) {
        if (params.get('party') !== String(partySession.startTime)) {
          params.set('party', String(partySession.startTime));
          updated = true;
        }
      } else if (params.has('party')) {
        params.delete('party');
        updated = true;
      }
    }

    if (updated) {
      const newUrl = `${window.location.pathname}?${params.toString()}`;
      try {
        window.history.replaceState(null, '', newUrl);
      } catch (e) {
        console.warn('Could not update URL history', e);
      }
    }
  }, [isRadioMode, currentManualItem?.id, partySession, items.length]);

  useEffect(() => {
    const init = async () => {
      const params = new URLSearchParams(window.location.search);
      const sharedId = params.get('item');
      const sharedTime = params.get('t');
      const partyTime = params.get('party');
      const radioParam = params.get('radio');

      const dataUrl = params.get('data') || DEFAULT_DATA_URL;

      let processedItems = items;
      let loadSuccess = false;

      // 1. Try Fetching Data
      if (dataUrl) {
        try {
          const res = await fetch(dataUrl);
          if (!res.ok) throw new Error('Network response was not ok');
          const rawData = await res.json();
          if (Array.isArray(rawData)) {
            processedItems = processData(rawData);
            setItems(processedItems);
            loadSuccess = true;
          }
        } catch (e) {
          console.warn(
            'Failed to fetch external data, falling back to local:',
            e
          );
        }
      }

      // 2. Fallback to Mock Data if Fetch Failed
      if (!loadSuccess) {
        try {
          const module = await import('./mock_data.js');
          if (module.default && Array.isArray(module.default)) {
            processedItems = processData(module.default);
            setItems(processedItems);
            loadSuccess = true;
          }
        } catch (e) {
          console.error('Failed to load local mock data', e);
        }
      }

      // 3. Logic for Deep Links

      // Check Radio Mode First
      if (radioParam === 'true') {
        setIsRadioMode(true);
        setShouldAutoPlay(true);
      }

      if (sharedId) {
        const found = processedItems.find(
          (i) => i.id === decodeURIComponent(sharedId)
        );
        if (found) {
          setCurrentManualItem(found);

          if (partyTime) {
            const startTime = parseInt(partyTime, 10);
            setPartySession({ itemId: found.id, startTime });
            setShouldAutoPlay(false);
          } else {
            if (sharedTime) {
              setHistory((prev) => {
                const meta = getHistoryMeta(prev, found.id);
                return {
                  ...prev,
                  [found.id]: {
                    progress: Number(sharedTime),
                    lastPlayed: meta.lastPlayed || Date.now(),
                  },
                };
              });
            }
            setShouldAutoPlay(true);
          }
        } else if (
          loadSuccess &&
          processedItems.length > 0 &&
          !currentManualItem
        ) {
          // If deep link failed but data loaded, fallback to first item
          setCurrentManualItem(processedItems[0]);
        }
      }
      // REMOVED the imperative fallback logic here to rely on the new Safety Effect
    };
    init();
  }, []); // Depend only on mount (history dependency intentionally omitted to avoid loops, though accessing stale history ref is possible but init logic handles this via closure or state setter if needed. Here we access state directly but init runs once. Actually accessing `history` state inside `useEffect` with `[]` deps will get initial value. This is okay as we primarily want to load FROM storage which `history` state is initialized with.)

  useEffect(() => {
    if (isRadioMode && currentRadioItem) {
      document.title = `Radio ${currentRadioItem.title}`;
    } else {
      if (partySession && partySession.itemId === currentManualItem?.id) {
        document.title = `Group Listening ${currentManualItem?.title}`;
      } else {
        if (currentManualItem)
          document.title = `Listening ${currentManualItem.title}`;
      }
    }
  }, [isRadioMode, currentRadioItem, currentManualItem, partySession]);

  useEffect(() => {
    if (!isRadioMode) return;
    const updateRadio = () => {
      const nowMs = Date.now();
      const offset = (nowMs - radioState.blockStartMs) / 1000;
      const activeItem = radioState.schedule.find(
        (i) => offset >= i.playStartOffset && offset < i.playEndOffset
      );
      if (activeItem && activeItem.uniqueKey !== currentRadioItem?.uniqueKey) {
        setCurrentRadioItem(activeItem);
      }
    };
    updateRadio();
    const interval = setInterval(updateRadio, 1000);
    return () => clearInterval(interval);
  }, [radioState, isRadioMode, currentRadioItem]);

  const handlePlay = (item) => {
    setIsRadioMode(false);
    setShouldAutoPlay(true);
    setCurrentManualItem(item);
  };

  const handleReturnToParty = () => {
    if (!partySession) return;
    const item = items.find((i) => i.id === partySession.itemId);
    if (item) {
      setIsRadioMode(false);
      setShouldAutoPlay(false);
      setCurrentManualItem(item);
    }
  };

  const handleQueue = (item) => {
    if (!manualQueue.find((i) => i.id === item.id))
      setManualQueue([...manualQueue, item]);
  };
  const removeFromQueue = (id) =>
    setManualQueue(manualQueue.filter((i) => i.id !== id));

  const toggleRadioMode = () => {
    if (isRadioMode) setShouldAutoPlay(false);
    else setShouldAutoPlay(true);
    setIsRadioMode(!isRadioMode);
  };

  if (items.length === 0) {
    return (
      <div className="min-h-screen bg-slate-950 text-slate-200 font-sans flex items-center justify-center flex-col gap-4">
        <Loader2 size={48} className="text-blue-500 animate-spin" />
        <h2 className="text-xl font-bold">Loading Library...</h2>
        <p className="text-slate-500 text-sm">Connecting to data source...</p>
      </div>
    );
  }

  const currentItem = isRadioMode
    ? currentRadioItem || { ...items[0], isLoading: true }
    : currentManualItem;
  const safeCurrentId = isRadioMode
    ? currentItem?.uniqueKey || currentItem?.id
    : currentItem?.id;

  if (!currentItem) return null;

  return (
    <div className="min-h-screen bg-slate-950 text-slate-200 font-sans selection:bg-blue-500/30 flex flex-col relative">
      <nav className="border-b border-slate-800 bg-slate-900/80 backdrop-blur sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="font-bold text-lg text-slate-100">
              BAWS <span className="text-blue-500">Audio</span>
            </span>
          </div>

          <div className="hidden md:flex items-center gap-6">
            <button
              onClick={() => setView('player')}
              className={`text-sm font-medium transition-colors ${view === 'player' ? 'text-blue-400' : 'text-slate-400 hover:text-slate-200'}`}
            >
              Player
            </button>
          </div>

          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2 bg-slate-950 border border-slate-800 rounded-full p-1 pr-3">
              <button
                onClick={toggleRadioMode}
                className={`flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider transition-all border ${!isRadioMode ? 'border-red-500 text-red-500 animate-[pulse_3s_ease-in-out_infinite] shadow-[0_0_15px_rgba(239,68,68,0.4)]' : 'bg-red-600 border-red-600 text-white shadow-md'}`}
              >
                <Radio
                  size={14}
                  className={isRadioMode ? 'animate-pulse' : ''}
                />{' '}
                {isRadioMode ? 'Radio Playing' : 'Play Radio'}
              </button>
            </div>
          </div>
        </div>
      </nav>

      <div className="md:hidden flex border-b border-slate-800 bg-slate-900/50">
        <button
          onClick={() => setView('player')}
          className={`flex-1 py-3 text-xs font-bold uppercase tracking-wide ${view === 'player' ? 'text-blue-400 border-b-2 border-blue-400' : 'text-slate-500'}`}
        >
          Player
        </button>
      </div>

      <main className="flex-grow max-w-7xl mx-auto px-4 py-6 w-full pb-24">
        <div className={view === 'player' ? 'block' : 'hidden'}>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            <div className="lg:col-span-8 flex flex-col gap-6">
              <AudioPlayer
                currentItem={currentItem}
                isRadioMode={isRadioMode}
                radioState={radioState}
                onNext={advanceManualTrack}
                onManualEnded={handleManualEnded}
                shouldAutoPlay={shouldAutoPlay}
                history={history}
                saveHistory={saveHistory}
                isMini={false}
                toggleFavorite={toggleFavorite}
                isFavorite={favorites.includes(currentItem.id)}
                onStartParty={(item) => setPartyItem(item)}
                partySession={partySession}
                savedBookmarks={
                  Array.isArray(bookmarks[currentItem.id])
                    ? bookmarks[currentItem.id]
                    : bookmarks[currentItem.id]
                      ? [bookmarks[currentItem.id]]
                      : []
                }
                onAddBookmark={handleAddBookmark}
              />
            </div>
            <div className="lg:col-span-4 flex flex-col h-full justify-start">
              <QueueCarousel
                queue={manualQueue}
                currentId={safeCurrentId}
                onPlay={handlePlay}
                onRemove={removeFromQueue}
                onReorder={handleReorder}
                onClear={() => setManualQueue([])}
                onSave={() => alert('Saved!')}
                isRadioMode={isRadioMode}
                radioState={radioState}
                history={history}
              />
            </div>
            <div className="lg:col-span-12">
              <ArchiveList
                items={items}
                onPlay={handlePlay}
                onQueue={handleQueue}
                onRemove={removeFromQueue}
                queue={manualQueue}
                history={history}
                toggleFavorite={toggleFavorite}
                favorites={favorites}
                onStartParty={(item) => setPartyItem(item)}
              />
            </div>
          </div>
        </div>

        {view === 'library' && (
          <div className="flex flex-col items-center justify-center h-96 text-slate-500">
            <BookOpen size={48} className="mb-4 opacity-50" />
            <h2 className="text-xl font-bold text-slate-300">Full Library</h2>
            <p className="mt-2">Browse collections, volumes, and speeches.</p>
          </div>
        )}

        {view === 'settings' && (
          <div className="flex flex-col items-center justify-center h-96 text-slate-500">
            <Settings size={48} className="mb-4 opacity-50" />
            <h2 className="text-xl font-bold text-slate-300">Settings</h2>
            <p className="mt-2">
              Audio quality, themes, and account preferences.
            </p>
          </div>
        )}

        {view !== 'player' && (
          <AudioPlayer
            currentItem={currentItem}
            isRadioMode={isRadioMode}
            radioState={radioState}
            onNext={advanceManualTrack}
            onManualEnded={handleManualEnded}
            shouldAutoPlay={shouldAutoPlay}
            history={history}
            saveHistory={saveHistory}
            isMini={true}
            onExpand={() => setView('player')}
            partySession={partySession}
            savedBookmarks={bookmarks[currentItem.id]}
            onAddBookmark={handleAddBookmark}
          />
        )}

        <SanghamittaModal
          isOpen={!!partyItem}
          onClose={() => setPartyItem(null)}
          item={partyItem}
        />

        {partySession &&
          partySession.itemId !== currentItem.id &&
          !isRadioMode && (
            <div className="fixed bottom-24 right-4 z-50 animate-bounce">
              <button
                onClick={handleReturnToParty}
                className="bg-blue-600 hover:bg-blue-500 text-white px-6 py-3 rounded-full font-bold shadow-lg flex items-center gap-2 transition-transform hover:scale-105"
              >
                <Users size={20} /> Return to Party <ArrowUpRight size={18} />
              </button>
            </div>
          )}
      </main>
    </div>
  );
}
